// MERA Foundry — product data + display metadata.
// Extracted from Foundry.jsx to keep the component focused on rendering logic.
// If brand copy expands further, move each brand into its own JSON file.

export const PILLAR_META = {
  all: { label: "All", color: "#0A0A0A" },
  memory: { label: "Memory", color: "#D4891A" },
  commerce: { label: "Commerce", color: "#00D4AD" },
  intelligence: { label: "Intelligence", color: "#0F766E" },
  governance: { label: "Governance", color: "#1F2937" },
  mastery: { label: "Mastery", color: "#6B8EF0" },
};

export const STATUS_META = {
  live: { label: "Live", color: "#0F766E" },
  soon: { label: "Launching Soon", color: "#D4891A" },
  review: { label: "Final Review", color: "#6B8EF0" },
};

export const PILLAR_ORDER = [
  "all",
  "memory",
  "commerce",
  "intelligence",
  "governance",
  "mastery",
];

export const BRANDS = [
  {
    id: "ryan",
    name: "Ryan",
    pillar: "memory",
    accent: "#D4891A",
    status: "live",
    url: "https://ryanremembers.com",
    tagline: "AI-powered enterprise assistant built for operational intelligence.",
    whatItDoes:
      "An AI-powered enterprise assistant that captures operational intelligence, surfaces what matters across complex organizations, and compounds institutional knowledge over time — so nothing important is ever lost between conversations.",
    whoItServes:
      "Enterprise operators, executives, and teams who need an intelligent system that remembers context, tracks decisions, and returns amplified intelligence at the exact moment it's needed.",
    statusDetail:
      "Live and actively iterating. RA-002 v1.4.0 in development.",
    pillarDescription:
      "Memory — the tools that capture what you learn, preserve what matters, and surface it when you need it. Intelligence that compounds with every interaction.",
    quote: "The enterprise doesn't forget. Now neither do you.",
  },
  {
    id: "uday",
    name: "Uday",
    pillar: "mastery",
    accent: "#6B8EF0",
    status: "live",
    url: "https://getuday.com",
    tagline: "The only LMS built natively for Career and Technical Education.",
    whatItDoes:
      "A learning management system built from the ground up for Career and Technical Education — not retrofitted from corporate training tools. Structured learning pathways from enrollment to employment outcomes, designed for every student regardless of institution size.",
    whoItServes:
      "CTE students, educators, and institutions across the United States. Every human who needs a credential pathway that leads somewhere real — not just a certificate on a shelf.",
    statusDetail:
      "Live. Free tier globally for under 50 students. Paid tiers for growing institutions. Global expansion underway: Mexico → Canada → Australia → Brazil.",
    pillarDescription:
      "Mastery — education infrastructure designed for outcomes, not completion metrics. The tool that develops what you can become.",
    quote: "The first LMS that remembers why education exists.",
  },
  {
    id: "kova",
    name: "KOVA",
    pillar: "governance",
    accent: "#B0413E",
    status: "soon",
    url: "https://kovahub.co",
    tagline: "Intelligence Mesh platform built on MCP architecture.",
    whatItDoes:
      "An Intelligence Mesh platform operating on MCP architecture — enabling enterprise-grade agentic AI with production-ready integrations, tiered intelligence depth, and governance controls built in from day one. The founding product of a new market category distinct from middleware, iPaaS, and API hubs.",
    whoItServes:
      "Enterprises deploying agentic AI at scale. Mid-market organizations who need the intelligence infrastructure without building it from scratch. Any organization where AI needs to be governed, not just deployed.",
    statusDetail:
      "Launching soon at kovahub.co. KOVA v5.1 — includes full \"Built for the MCP Era\" architecture. The Constellation is open to everyone; we charge for the depth of intelligence that flows through it.",
    pillarDescription:
      "Governance — the integration and intelligence layer that makes every other MERA product trustworthy at enterprise scale.",
    quote:
      "The Constellation is open to everyone. We charge for the depth of intelligence that flows through it.",
  },
  {
    id: "eulr",
    name: "EULR",
    pillar: "commerce",
    accent: "#2E8B57",
    status: "review",
    url: "https://eulr.io",
    tagline: "AI-native fintech infrastructure for trading identification.",
    whatItDoes:
      "AI-native fintech infrastructure providing unique identification and compliance architecture for trading and financial transactions — a EULR number for every trade, every entity, every transaction that needs to be precisely tracked and verified.",
    whoItServes:
      "Traders, fintech operators, and financial institutions who need reliable, unique identification infrastructure built for the speed and complexity of modern markets.",
    statusDetail: "Final review. Deploying to eulr.io. 108/108 tests passing.",
    pillarDescription:
      "Commerce — the tools that protect what you earn and give every human the financial infrastructure previously reserved for institutions.",
    quote: "Every transaction deserves an identity. EULR provides it.",
  },
  {
    id: "folio",
    name: "Fol.io",
    pillar: "commerce",
    accent: "#00D4AD",
    status: "review",
    url: "https://usefolio.work",
    tagline:
      "Freelance billing and financial operations for independent operators.",
    whatItDoes:
      "Billing, invoicing, and financial operations built for freelancers, independent operators, and solo businesses — giving every individual the commercial infrastructure that enterprises take for granted, without the enterprise overhead.",
    whoItServes:
      "Every human who earns independently — freelancers, consultants, creators, and operators who deserve financial tools built for how they actually work, not how corporations work.",
    statusDetail: "Final review. Built and approaching deployment.",
    pillarDescription:
      "Commerce — protecting what you earn and treating one person with the same financial precision previously reserved for institutions.",
    quote: "Your work has value. Fol.io makes sure it gets paid.",
  },
  {
    id: "cortex",
    name: "Cortex",
    pillar: "intelligence",
    accent: "#0F766E",
    status: "review",
    tagline:
      "Workforce behavioral intelligence derived from how work actually happens.",
    whatItDoes:
      "Workforce behavioral intelligence that sees what is actually happening inside project teams — not what people report, but what the work reveals. Derived from real behavioral signals across project delivery, communication patterns, and collaboration activity.",
    whoItServes:
      "Enterprise teams, HR leaders, and organizations who need to understand how their people actually work — not how they describe their work in a performance review.",
    statusDetail:
      "Final review. The data engine that feeds PTRN and MATR across the Intelligence pillar.",
    pillarDescription:
      "Intelligence — behavioral signals that compound over time. The raw data layer that makes every intelligence product in MERA smarter.",
    quote:
      "The work knows things that people won't say. Cortex listens to the work.",
  },
  {
    id: "ptrn",
    name: "PTRN",
    pillar: "intelligence",
    accent: "#8B5E3C",
    status: "review",
    tagline:
      "Human Potential Index — behavioral evidence turned into talent intelligence.",
    whatItDoes:
      "Translates behavioral work data into a comprehensive, evidence-based Human Potential Index for every person in an organization — derived entirely from what the work reveals, not what managers report or employees self-assess.",
    whoItServes:
      "Organizations who want to know whether they have the right humans doing the right work in the right way — and whether each person has met, exceeded, or has yet to reach their demonstrated potential.",
    statusDetail:
      "Final review. Sits above Cortex and below MATR in the three-tier Intelligence architecture. No performance review required.",
    pillarDescription:
      "Intelligence — human potential made quantifiable, longitudinal, and actionable for the first time without bias or recency effect.",
    quote:
      "Potential is not what people say about themselves. PTRN reads what the work says instead.",
  },
  {
    id: "trnslt",
    name: "TRNSLT",
    pillar: "intelligence",
    accent: "#0D9488",
    status: "review",
    tagline:
      "Enterprise data transformation for requirement-specific system ingestion.",
    whatItDoes:
      "Converts raw data into refined, requirement-specific loads for enterprise system ingestion — transforming what organizations have into what their systems actually need, without the manual overhead and error rate of traditional data transformation processes.",
    whoItServes:
      "Enterprise data teams, integration architects, and organizations running complex ERP and platform environments who need data in the right shape at the right time for the right system.",
    statusDetail:
      "Final review. Part of the MERA Intelligence pillar alongside Cortex and PTRN.",
    pillarDescription:
      "Intelligence — connecting what is siloed and converting raw signal into structured, usable intelligence across every enterprise system.",
    quote: "Data that doesn't fit is data that doesn't work. TRNSLT makes it fit.",
  },
  {
    id: "railwright",
    name: "Railwright",
    pillar: "governance",
    accent: "#1F2937",
    status: "review",
    tagline: "AI governance and compliance layer for enterprise deployment.",
    whatItDoes:
      "The AI governance and compliance layer that makes agentic AI deployable at enterprise scale — policy engine, audit trail, governance controls, and compliance architecture for every AI environment an organization builds or operates.",
    whoItServes:
      "Enterprise AI teams, compliance officers, and organizations in regulated industries — healthcare, financial services, government — who need AI that can be trusted, audited, and governed without being slowed down.",
    statusDetail:
      "Final review. Paired with KOVA in the Governance pillar — KOVA governs how systems connect, Railwright governs how AI operates inside them.",
    pillarDescription:
      "Governance — the policy and compliance infrastructure that makes every MERA product trustworthy at enterprise scale.",
    quote: "AI that can't be governed can't be deployed. Railwright solves that.",
  },
  {
    id: "vernai",
    name: "Vernai",
    pillar: "mastery",
    accent: "#C7567C",
    status: "review",
    tagline: "AI-powered language intelligence that adapts voice and precision.",
    whatItDoes:
      "AI-powered language intelligence that adapts voice, tone, and communication precision so every message lands exactly as intended — across languages, registers, and organizational contexts. Communication that doesn't translate is communication that doesn't work.",
    whoItServes:
      "Every human who communicates — from the individual writer to the enterprise team to the institution publishing at global scale. Language is the final mile of every idea.",
    statusDetail:
      "Final review. Paired with Uday in the Mastery pillar — Uday develops capability, Vernai ensures it's communicated with precision.",
    pillarDescription:
      "Mastery — developing human capability through education and communication intelligence that closes the gap between potential and expression.",
    quote: "Every language has a version of what you mean. Vernai finds it.",
  },
];

// Append UTM params to outbound "Visit" links so downstream product analytics
// can see exactly how much traffic MERA's foundry page sent them.
export const withOutboundUtm = (url, brandId) => {
  if (!url) return url;
  try {
    const u = new URL(url);
    u.searchParams.set("utm_source", "meratechnology");
    u.searchParams.set("utm_medium", "foundry");
    u.searchParams.set("utm_campaign", brandId);
    return u.toString();
  } catch {
    return url;
  }
};

export const prettyDomain = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};
